<template>
  <b-modal
    :visible="value"
    size="xl"
    scrollable
    hide-footer
    centered
    no-close-on-backdrop
    no-close-on-esc
    @input="onClose"
    @close="onClose"
  >
    <template #modal-title>
      {{ partnerName }}
      <b-badge :variant="partnerExperienceLevel(completedJobs).variant">
        {{ partnerExperienceLevel(completedJobs).text }}
      </b-badge>
    </template>

    <b-row>
      <b-col cols="4">
        <b-form-group :label="`${$t('filter')}:`" class="mb-0">
          <b-form-input
            :value="searchTerm"
            @input="performSearch"
            :placeholder="$t('type-to-search')"
          />
        </b-form-group>
      </b-col>
      <b-col cols="3">
        <b-form-group :label="`${$t('owner')}:`" class="mb-0">
          <b-form-select v-model="filterOwner" :options="owners">
            <template slot="first">
              <option :value="null">-- {{ $t('all') }} --</option>
            </template>
          </b-form-select>
        </b-form-group>
      </b-col>
      <b-col cols="2" class="d-flex flex-column justify-content-end">
        <b-form-checkbox v-model="filterUseAdvancedSearch" class="mb-1">
          {{ $t('filters.advanced.toggle') }}
        </b-form-checkbox>
      </b-col>
    </b-row>

    <div v-if="filterUseAdvancedSearch" class="advanced-search-container">
      <advanced-search
        v-model="filterAdvancedSearch"
        :filter-options="advancedFilterOptions"
      />
    </div>

    <b-row class="mb-3 mt-4">
      <b-col>
        {{ $t('selected-jobs-title', { count: selectedJobsCount }) }}
      </b-col>
    </b-row>

    <b-card no-body>
      <b-tabs card active-nav-item-class="font-weight-bold">
        <b-tab
          :title="$t('job.tabs.shown')"
          class="p-0"
          lazy
          :active="getActiveTab('shown')"
        >
          <PartnerContactJobsTable
            :partnerId="partnerId"
            :contacts="contacts"
            :searchTerm="searchTerm"
            :selectedJobs="selectedJobs"
            :showActions="false"
            :filterAdvancedSearch="filterAdvancedSearch"
            :filterUseAdvancedSearch="filterUseAdvancedSearch"
            :filterOwner="filterOwner"
            :includeChildren="includeChildren"
            endpoint="shown-jobs"
            @on-partner-call="onPartnerCall"
            @tab-change="searchTerm = ''"
            @job-selected="handleJobSelection"
            @comment-section-state="handleCommentSectionState"
          />
        </b-tab>
        <b-tab
          :title="$t('job.tabs.potential')"
          class="p-0"
          lazy
          :active="getActiveTab('potential')"
        >
          <PartnerContactJobsTable
            :partnerId="partnerId"
            :contacts="contacts"
            :searchTerm="searchTerm"
            :selectedJobs="selectedJobs"
            :showActions="true"
            :filterAdvancedSearch="filterAdvancedSearch"
            :filterUseAdvancedSearch="filterUseAdvancedSearch"
            :filterOwner="filterOwner"
            :includeChildren="includeChildren"
            endpoint="potential-jobs"
            @on-partner-call="onPartnerCall"
            @tab-change="searchTerm = ''"
            @job-selected="handleJobSelection"
            @comment-section-state="handleCommentSectionState"
          />
        </b-tab>
        <b-tab
          :title="$t('job.tabs.active')"
          class="p-0"
          lazy
          :active="getActiveTab('active')"
        >
          <PartnerContactJobsTable
            :partnerId="partnerId"
            :contacts="contacts"
            :searchTerm="searchTerm"
            :selectedJobs="selectedJobs"
            :showActions="false"
            :filterAdvancedSearch="filterAdvancedSearch"
            :filterUseAdvancedSearch="filterUseAdvancedSearch"
            :filterOwner="filterOwner"
            :includeChildren="includeChildren"
            endpoint="active-jobs"
            @on-partner-call="onPartnerCall"
            @tab-change="searchTerm = ''"
            @job-selected="handleJobSelection"
            @comment-section-state="handleCommentSectionState"
          />
        </b-tab>
        <b-tab
          :title="$t('job.tabs.done')"
          class="p-0"
          lazy
          :active="getActiveTab('completed')"
        >
          <PartnerContactJobsTable
            :partnerId="partnerId"
            :contacts="contacts"
            :searchTerm="searchTerm"
            :selectedJobs="selectedJobs"
            :showActions="false"
            :filterAdvancedSearch="filterAdvancedSearch"
            :filterUseAdvancedSearch="filterUseAdvancedSearch"
            :filterOwner="filterOwner"
            :includeChildren="includeChildren"
            endpoint="completed-jobs"
            @on-partner-call="onPartnerCall"
            @tab-change="searchTerm = ''"
            @job-selected="handleJobSelection"
            @comment-section-state="handleCommentSectionState"
          />
        </b-tab>
        <b-tab
          :title="$t('job.tabs.rejected')"
          class="p-0"
          lazy
          :active="getActiveTab('rejected')"
        >
          <PartnerContactJobsTable
            :partnerId="partnerId"
            :contacts="contacts"
            :searchTerm="searchTerm"
            :selectedJobs="selectedJobs"
            :showActions="false"
            :filterAdvancedSearch="filterAdvancedSearch"
            :filterUseAdvancedSearch="filterUseAdvancedSearch"
            :filterOwner="filterOwner"
            :includeChildren="includeChildren"
            endpoint="rejected-jobs"
            @on-partner-call="onPartnerCall"
            @tab-change="searchTerm = ''"
            @job-selected="handleJobSelection"
            @comment-section-state="handleCommentSectionState"
          />
        </b-tab>
        <b-tab
          :title="$t('job.tabs.cancelled')"
          class="p-0"
          lazy
          :active="getActiveTab('cancelled')"
        >
          <PartnerContactJobsTable
            :partnerId="partnerId"
            :contacts="contacts"
            :searchTerm="searchTerm"
            :selectedJobs="selectedJobs"
            :showActions="false"
            :filterAdvancedSearch="filterAdvancedSearch"
            :filterUseAdvancedSearch="filterUseAdvancedSearch"
            :filterOwner="filterOwner"
            :includeChildren="includeChildren"
            endpoint="cancelled-jobs"
            @on-partner-call="onPartnerCall"
            @tab-change="searchTerm = ''"
            @job-selected="handleJobSelection"
            @comment-section-state="handleCommentSectionState"
          />
        </b-tab>
        <b-tab
          :title="$t('job.tabs.rainy-potential')"
          class="p-0"
          lazy
          :active="getActiveTab('rainy-potential')"
        >
          <PartnerContactJobsTable
            :partnerId="partnerId"
            :contacts="contacts"
            :searchTerm="searchTerm"
            :selectedJobs="selectedJobs"
            :showActions="true"
            :filterAdvancedSearch="filterAdvancedSearch"
            :filterUseAdvancedSearch="filterUseAdvancedSearch"
            :filterOwner="filterOwner"
            :includeChildren="includeChildren"
            endpoint="rainy-potential-jobs"
            @on-partner-call="onPartnerCall"
            @tab-change="searchTerm = ''"
            @job-selected="handleJobSelection"
            @comment-section-state="handleCommentSectionState"
          />
        </b-tab>
      </b-tabs>
    </b-card>
  </b-modal>
</template>

<script>
import { mapGetters } from 'vuex';
import { debounce } from 'lodash';
import { _partnerExperienceLevel } from '@gid/vue-common/utils';
import { JobTypeEnum, ContactProfileEnum } from '@gid/models';
import { CondOperator, RequestQueryBuilder } from '@dataui/crud-request';
import AdvancedSearch from '@gid/vue-common/components/filters/AdvancedSearch.vue';
import PartnerContactJobsTable from '@/components/partner/PartnerContactJobsTable.vue';
import axios from 'axios';
import Vue from 'vue';

Vue.component('CrudMultiSelect', () =>
  import('@gid/vue-common/components/filters/CrudMultiSelect.vue'),
);

export default {
  components: {
    PartnerContactJobsTable,
    AdvancedSearch,
  },
  props: {
    partnerId: String,
    partnerName: String,
    contacts: Array,
    completedJobs: Number,
    value: Boolean,
    activeTab: String,
    includeChildren: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchTerm: '',
      currentPage: 1,
      selectedJobs: [], // track ids of selected jobs per page
      hasUnsavedComments: false,
      filterUseAdvancedSearch: false,
      filterAdvancedSearch: {},
      filterOwner: null,
      owners: [],
      loading: false,
      advancedFilterOptions: [
        {
          label: this.$i18n.t('filters.brand'),
          type: 'job.brand__c',
          component: 'CrudMultiSelect',
          endpoint: '/data-api/brand',
          searchFields: ['id', 'name'],
          trackBy: 'id',
          selectLabel: 'name',
          multiple: true,
          initFetchLabels: true,
        },
        {
          label: this.$i18n.t('filters.job-name'),
          type: 'job.name',
          component: 'CrudMultiSelect',
          endpoint: '/data-api/job',
          searchFields: ['name'],
          trackBy: 'name',
          selectLabel: 'name',
          multiple: true,
          initFetchLabels: true,
        },
        {
          label: this.$i18n.t('filters.customer'),
          type: 'job.customer__c',
          component: 'CrudMultiSelect',
          endpoint: '/data-api/customer',
          searchFields: ['firstName', 'lastName'],
          trackBy: 'id',
          selectLabel: 'firstName',
          multiple: true,
          initFetchLabels: true,
        },
        {
          label: this.$i18n.t('filters.customer-postal-code'),
          type: 'customer.shippingpostalcode',
          component: 'b-form-input',
        },
        {
          label: this.$i18n.t('filters.customer-city'),
          type: 'customer.shippingcity',
          component: 'b-form-input',
        },
        {
          label: this.$i18n.t('filters.project-name'),
          type: 'p.name',
          component: 'b-form-input',
        },
        {
          label: this.$i18n.t('filters.external-object-id'),
          type: 'job.eid__c',
          component: 'b-form-input',
        },
        {
          label: this.$i18n.t('filters.external-invoicing-id'),
          type: 'job.invoicing_eid__c',
          component: 'b-form-input',
        },
        {
          label: this.$i18n.t('filters.external-partner-eid'),
          type: 'job.partner_eid__c',
          component: 'b-form-input',
        },
        {
          label: this.$i18n.t('inputs.headings.affiliate_id'),
          type: 'job.affiliate__c',
          component: 'CrudMultiSelect',
          endpoint: '/data-api/affiliate-id',
          searchFields: ['id', 'affiliateId'],
          trackBy: 'affiliateId',
          selectLabel: 'affiliateId',
          multiple: true,
          initFetchLabels: true,
        },
        {
          label: this.$i18n.t('filters.opportunity'),
          type: 'job.opportunity__c',
          component: 'CrudMultiSelect',
          endpoint: '/data-api/opportunity',
          searchFields: ['id', 'name'],
          trackBy: 'id',
          selectLabel: 'name',
          multiple: true,
          initFetchLabels: true,
        },
        {
          label: this.$i18n.t('filters.product'),
          type: 'jp.product_id',
          component: 'CrudMultiSelect',
          endpoint: '/data-api/product',
          searchFields: ['name'],
          trackBy: 'id',
          selectLabel: 'name',
          multiple: true,
          initFetchLabels: true,
        },
        {
          label: this.$i18n.t('filters.product-serial-number'),
          type: 'jp.serial_number',
          component: 'b-form-input',
        },
        {
          label: this.$i18n.t('filters.service'),
          type: 'js.service__c',
          component: 'CrudMultiSelect',
          endpoint: '/data-api/service',
          searchFields: ['id', 'name'],
          trackBy: 'id',
          selectLabel: 'descriptionDe',
          customLabel: (item) => {
            return `[${item.opportunity.name}] ${item.descriptionDe}`;
          },
          multiple: true,
          initFetchLabels: true,
        },
      ],
      filterCombinations: [
        {
          text: this.$t('filters.precheck'),
          type: 'job_type',
          value: JobTypeEnum.PRECHECK,
        },
        {
          text: this.$t('filters.remote-precheck'),
          type: 'job_type',
          value: 'rpr',
        },
        {
          text: this.$t('filters.onsite-precheck'),
          type: 'job_type',
          value: 'onsitepr',
        },
        {
          text: this.$t('filters.installation'),
          type: 'job_type',
          value: JobTypeEnum.INSTALLATION,
        },
        {
          text: this.$t('filters.hardware'),
          type: 'job_type',
          value: JobTypeEnum.HARDWARE_SALE,
        },
        {
          text: this.$t('filters.activation'),
          type: 'job_type',
          value: JobTypeEnum.ACTIVATION,
        },
        {
          text: this.$t('remote-job'),
          type: 'job_type',
          value: JobTypeEnum.REMOTE,
        },
        {
          text: this.$t('filters.maintenance'),
          type: 'job_type',
          value: JobTypeEnum.MAINTENANCE,
        },
        {
          text: this.$t('filters.defect-elimination'),
          type: 'job_type',
          value: JobTypeEnum.DEFECT_ELIMINATION,
        },
        {
          text: this.$t('filters.reclamation'),
          type: 'job_type',
          value: 'reclamation',
        },
      ],
    };
  },
  computed: {
    ...mapGetters(['locale']),
    selectedJobsCount() {
      return this.selectedJobs.length;
    },
    partnerExperienceLevel() {
      return _partnerExperienceLevel;
    },
  },
  created() {
    // Load owners
    const gidAccountId = 1;
    const queryString = RequestQueryBuilder.create({
      filter: [
        {
          field: 'accountId',
          operator: CondOperator.EQUALS,
          value: gidAccountId,
        },
      ],
      fields: ['id', 'name', 'profiles'],
      sort: [{ field: 'name', order: 'ASC' }],
    }).query();

    axios.get(`/data-api/contact?${queryString}`).then((response) => {
      if (response.data.length) {
        this.owners = response.data
          .filter((record) =>
            record.profiles.some(
              ({ profile }) =>
                profile === ContactProfileEnum.PROJECT_MANAGER_GENERIC,
            ),
          )
          .map(({ id, name }) => ({
            value: id,
            text: name,
          }));
      }
    });
  },
  methods: {
    /** Emit action to Parent cmp to Trigger API call when user types in search bar  **/
    performSearch: debounce(function (value) {
      this.searchTerm = value;
    }, 600),
    /** Partner Modal close action **/
    onClose() {
      if (this.hasUnsavedComments) {
        this.onPartnerCall(this.selectedJobs.map((job) => job.api_id));
        return;
      }
      this.$emit('close', {
        selectedJobsIds: this.selectedJobs.map((job) => job.api_id),
        isCalled: false,
      });
      this.close();
    },
    getActiveTab(tabName) {
      return this.activeTab === tabName;
    },
    onPartnerCall(selectedJobsIds) {
      this.close();
      this.$emit('close', { selectedJobsIds, isCalled: true });
    },
    close() {
      this.selectedJobs = [];
      this.currentPage = 1;
      this.searchTerm = null;
      this.hasUnsavedComments = false;
      this.filterUseAdvancedSearch = false;
      this.filterAdvancedSearch = {};
      this.filterOwner = null;
    },
    handleJobSelection(val) {
      this.selectedJobs = val;
    },
    handleCommentSectionState({ isVisible }) {
      this.hasUnsavedComments = isVisible;
    },
  },
  watch: {
    filterOwner(newVal, oldVal) {
      if (newVal !== oldVal) {
        // Trigger search update in all tabs
        this.$forceUpdate();
      }
    },
  },
};
</script>

<style scoped>
/* Make advanced search dropdown scrollable */
::v-deep .advanced-search-container .dropdown-menu {
  max-height: 300px;
  overflow-y: auto;
}
</style>
